export const orgCopy = {
  navOrganization: "Организация",
  navDepartments: "Отделы",
  navGroups: "Группы",
  organizationTitle: "Организация",
  organizationDescription: "Название, часовой пояс и доступ к новым файлам.",
  sectionGeneral: "Организация",
  sectionGeneralHelp:
    "Название видно сотрудникам в шапке. Идентификатор — технический код, его нельзя сменить.",
  save: "Сохранить",
  saved: "Сохранено",
  slugLabel: "Идентификатор",
  slugHelp: "Технический код организации.",
  visibilityLabel: "Доступ к новым файлам",
  visibilityOrganization: "Могут читать все сотрудники",
  visibilityDepartment: "Могут читать только сотрудники отдела",
  visibilityHelp:
    "Так открывается новый файл. Уже загруженные не меняются. При загрузке можно выбрать иначе.",
  timeZoneLabel: "Часовой пояс",
  timeZoneHelp:
    "Запасной пояс, если браузер сотрудника ещё не сообщил свой. Даты на экране — в поясе сотрудника.",
  invalidTimeZone: "Выберите часовой пояс",
  departmentsTitle: "Отделы",
  departmentsDescription:
    "Сотрудники отдела получают доступ, который выдан отделу.",
  groupsTitle: "Группы",
  groupsDescription:
    "Группа — набор сотрудников для общего доступа к документам.",
  create: "Создать",
  createDepartment: "Создать отдел",
  createGroup: "Создать группу",
  cancel: "Отмена",
  pending: "Сохраняем…",
  created: "Создано",
  add: "Добавить",
  remove: "Убрать",
  nameLabel: "Название",
  departmentLabel: "Отдел",
  groupLabel: "Группа",
  noDepartment: "Без отдела",
  userLabel: "Сотрудник",
  searchUsers: "Найти сотрудника",
  invalidName: "Укажите название",
  nameTaken: "Такое название уже есть",
  missingUser: "Выберите сотрудника",
  alreadyMember: "Этот сотрудник уже в составе",
  denied: "Недостаточно прав",
  missing: "Не найдено",
  errorTitle: "Организация",
  emptyDepartmentsTitle: "Пока нет отделов",
  emptyDepartmentsDescription: "Создайте отдел, затем добавьте сотрудников.",
  emptyGroupsTitle: "Пока нет групп",
  emptyGroupsDescription: "Создайте группу, затем добавьте сотрудников.",
  emptyMembers: "В составе никого нет.",
  emptyFilterTitle: "Ничего не найдено",
  emptyFilterDescription: "Измените запрос.",
  columnName: "Название",
  columnMembers: "Сотрудники",
  open: "Открыть",
  searchDepartments: "Найти отдел",
  searchGroups: "Найти группу",
  backDepartments: "К отделам",
  backGroups: "К группам",
  searchUser: "Имя или email",
  searchNamed: "Название",
} as const;

export type OrgUserOption = {
  id: string;
  name: string;
  email: string;
};

export function adminOrganizationHref(): string {
  return "/admin/organization";
}

export function adminDepartmentsHref(): string {
  return "/admin/departments";
}

export function adminDepartmentHref(id: string): string {
  return `/admin/departments/${id}`;
}

export function adminGroupsHref(): string {
  return "/admin/groups";
}

export function adminGroupHref(id: string): string {
  return `/admin/groups/${id}`;
}

export function parseOrgName(
  raw: string,
): { ok: true; value: string } | { ok: false; error: string } {
  const name = raw.trim().replace(/\s+/g, " ");
  if (!name || name.length > 80) {
    return { ok: false, error: orgCopy.invalidName };
  }
  return { ok: true, value: name };
}

export const DEFAULT_ORG_TIMEZONE = "Europe/Moscow";

export const ORG_TIMEZONES = [
  { id: "Europe/Kaliningrad", label: "Калининград (UTC+2)" },
  { id: "Europe/Moscow", label: "Москва (UTC+3)" },
  { id: "Europe/Samara", label: "Самара (UTC+4)" },
  { id: "Asia/Yekaterinburg", label: "Екатеринбург (UTC+5)" },
  { id: "Asia/Omsk", label: "Омск (UTC+6)" },
  { id: "Asia/Krasnoyarsk", label: "Красноярск (UTC+7)" },
  { id: "Asia/Irkutsk", label: "Иркутск (UTC+8)" },
  { id: "Asia/Yakutsk", label: "Якутск (UTC+9)" },
  { id: "Asia/Vladivostok", label: "Владивосток (UTC+10)" },
  { id: "Asia/Magadan", label: "Магадан (UTC+11)" },
  { id: "Asia/Kamchatka", label: "Камчатка (UTC+12)" },
] as const;

export function parseOrgTimeZone(raw: string): string | null {
  return ORG_TIMEZONES.find((zone) => zone.id === raw.trim())?.id ?? null;
}

export function resolveOrgTimeZone(raw: string | null | undefined): string {
  return parseOrgTimeZone(raw ?? "") ?? DEFAULT_ORG_TIMEZONE;
}

export function parseOptionalId(raw: string): string | null {
  const id = raw.trim();
  return id.length > 0 ? id : null;
}

export function parseUserIds(formData: FormData): string[] {
  const ids = formData
    .getAll("userId")
    .map((value) => String(value).trim())
    .filter(Boolean);
  return [...new Set(ids)];
}

export function isUniqueNameError(error: unknown): boolean {
  return (
    typeof error === "object" &&
    error !== null &&
    "code" in error &&
    (error as { code: string }).code === "P2002"
  );
}

export function usersNotIn<T extends { id: string }>(
  users: T[],
  memberIds: readonly string[],
): T[] {
  const taken = new Set(memberIds);
  return users.filter((user) => !taken.has(user.id));
}

export function matchesComboItem(
  text: string,
  inputValue: string | null | undefined,
): boolean {
  const needle = (inputValue ?? "").trim().toLowerCase();
  if (!needle) {
    return true;
  }
  return text.toLowerCase().includes(needle);
}

export function memberComboText(user: { name: string; email: string }): string {
  return `${user.name} · ${user.email}`;
}

export function filterNamed<T extends { name: string }>(
  rows: T[],
  query: string,
): T[] {
  const needle = query.trim().toLowerCase();
  if (!needle) {
    return rows;
  }
  return rows.filter((row) => row.name.toLowerCase().includes(needle));
}

export function orgMembersLabel(count: number): string {
  const n = Math.abs(count) % 100;
  const n1 = n % 10;
  if (n > 10 && n < 20) {
    return `${count} сотрудников`;
  }
  if (n1 === 1) {
    return `${count} сотрудник`;
  }
  if (n1 >= 2 && n1 <= 4) {
    return `${count} сотрудника`;
  }
  return `${count} сотрудников`;
}
